/**
 * Production-pipeline stage of a board. Every board moves through these in
 * order (though any transition is allowed); a NEW board starts at
 * "scripting". Stored as relational board metadata (not in the Yjs doc):
 * the dashboard needs it without opening any board's doc.
 */
export const BOARD_STATUSES = [
    'scripting',
    'storyboarding',
    'request_review',
    'shooting',
    'editing',
];
export const BOARD_STATUS_LABELS = {
    scripting: 'Scripting',
    storyboarding: 'Storyboarding',
    request_review: 'Request Review',
    shooting: 'Shooting',
    editing: 'Editing',
};
export function isBoardStatus(value) {
    return BOARD_STATUSES.includes(value);
}
/**
 * Is the scoring done? Every run covered, and at least one run —
 * `BgmCoverage.ready`'s rule restated over the two counts, so a caller who
 * has only the summary (the dashboard card, the desktop Projects page) can
 * never disagree with the board about what "done" means.
 */
export function bgmStemsDone(stems) {
    return stems.total > 0 && stems.covered >= stems.total;
}
/**
 * The stages whose cards carry the BGM stems tag. Scoring begins when the
 * shoot does and runs on into the edit; before the shoot there is usually no
 * finished script to score, and a tag on every card would say nothing.
 */
export const BGM_STEMS_STAGES = ['shooting', 'editing'];
export function showsBgmStems(board) {
    return BGM_STEMS_STAGES.includes(board.status);
}
/**
 * The stage a board auto-advances INTO when the storyboard is approved. Not
 * `editing` and not a config knob: shooting is what "the plan is signed off"
 * means. WHO the resulting task lands on IS configurable — see the shooting
 * owners preset below.
 */
export const STORYBOARD_APPROVED_STATUS = 'shooting';
/**
 * Statuses that REOPEN the storyboard gate when a board is moved into them.
 * Moving back to the drawing board (literally) must not leave a stale
 * approval that would re-fire the auto-advance the moment BGM lands.
 */
export const STORYBOARD_GATE_RESET_STATUSES = [
    'scripting',
    'storyboarding',
];
/**
 * F-146: the "Create a chat room" checkbox's starting state (owner decision,
 * 2026-10-08): checked for a long-form video, unchecked for a Short — one
 * board per Short (F-143) would otherwise mean one channel per Short.
 */
export function defaultCreateChatRoom(kind) {
    return kind === 'long';
}
/** `YYYY-MM-DD` — the shape of `scriptFinishDate` and of rainvic task deadlines. */
export const CALENDAR_DATE_PATTERN = '^\\d{4}-\\d{2}-\\d{2}$';
/**
 * Is this a real calendar date written as `YYYY-MM-DD`? The pattern alone
 * lets `2026-02-31` through; this rebuilds the date in UTC and checks that
 * nothing rolled over, so a typo cannot become a deadline nobody can meet.
 */
export function isCalendarDateKey(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const [year, month, day] = value.split('-').map(Number);
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return false;
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day);
}
/**
 * Cap on the preset. A shooting task with twenty owners already means
 * "nobody"; the bound exists to keep a paste from writing an unbounded row.
 */
export const SHOOTING_OWNERS_MAX = 20;
/* ------------------------------------------------------------------ */
/* Admin: the standing Shorts crew (F-142)                              */
/* — packages/server/src/api/admin-shorts-crew.ts                       */
/* ------------------------------------------------------------------ */
/**
 * The three roles a Shorts week hands work to, in pipeline order. The crew
 * is the same people every week, so the project owner sets them once and
 * every create and stage move on a Shorts board PREFILLS its assignee picker
 * from them (docs/11 §4.4) — the dialog still opens: a prefilled list is a
 * confirmation, a skipped dialog is a surprise.
 */
export const SHORTS_CREW_ROLES = ['writers', 'shooters', 'editors'];
/** Cap per role, for the same reason as `SHOOTING_OWNERS_MAX`. */
export const SHORTS_CREW_MAX = 20;
/** An empty crew — what a workspace has before anyone sets one. */
export function emptyShortsCrew() {
    return { writers: [], shooters: [], editors: [] };
}
/**
 * Which role owns a stage of a Shorts week — the prefill rule. Writers own
 * everything up to the shoot (scripting, and the storyboard stages a team
 * may still use), shooters the shoot, editors the edit.
 */
export function shortsCrewRoleFor(status) {
    switch (status) {
        case 'shooting':
            return 'shooters';
        case 'editing':
            return 'editors';
        default:
            return 'writers';
    }
}
/**
 * Audio upload cap for POST /api/boards/:id/audio-assets — larger than
 * `MAX_ASSET_BYTES` (20 MB, set for images), and pinned UNDER Cloud Run's
 * 32 MiB HTTP/1 request ceiling (docs/06 §4 #5; the service runs HTTP/1 for
 * its WebSockets). The route proxies the bytes, so anything bigger is refused
 * at Google's edge before Kola ever sees it — the earlier 50 MB figure was a
 * promise the deployment could not keep, and read as "the upload does not
 * accept my file". A longer lossless stem needs a signed-URL direct-to-GCS
 * path (docs/02 F-126, not built).
 */
export const MAX_AUDIO_BYTES = 30 * 1024 * 1024;
/**
 * Canonical account key: logins that differ only by letter case, whitespace
 * runs, or Unicode composition (NFC vs NFD input, e.g. macOS vs iOS IMEs)
 * must resolve to the SAME user id — one account, one user, one avatar color,
 * one comment-author identity. Client (`core/identity.ts`) and server
 * (`core/auth/dev.ts`) both key identity through this, so the two sides can
 * never disagree on who "Kim Lee" is.
 */
export function canonicalAccountKey(raw) {
    return raw.normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();
}
/**
 * Upload cap for POST /api/boards/:id/assets (F-30) — enforced server-side
 * as the route's bodyLimit (413 beyond). Shared so the client's error copy
 * and any pre-flight checks can never drift from the enforced limit.
 */
export const MAX_ASSET_BYTES = 20 * 1024 * 1024;
/**
 * Upload cap for POST /api/boards/:id/files (F-135 — any file). The same
 * figure as `MAX_AUDIO_BYTES`, for the same reason: Cloud Run's HTTP/1
 * request ceiling is 32 MiB and the route proxies the bytes (docs/06 §4 #5),
 * so no route on this server can honour a larger promise. A bigger video
 * needs the signed-URL direct-to-GCS upload docs/02 F-126 names — not built.
 */
export const MAX_FILE_BYTES = 30 * 1024 * 1024;
/**
 * Longest file name a `file` element (or an image's `name`) records — the
 * common filesystem limit, so a download is never refused for its name.
 */
export const FILE_NAME_MAX_LENGTH = 255;
/* ------------------------------------------------------------------ */
/* Integration API (rainvic-tool ↔ Kola) — packages/server/src/api/    */
/* integration.ts. DTOs live here so the rainvic-tool renderer can     */
/* share them type-only (repo README "Repo layout").                   */
/* ------------------------------------------------------------------ */
/**
 * Rows one `POST /api/boards/:id/import-rows` may carry, and per-cell text
 * length. The row cap matches docs/01 §scale's board ceiling (and the
 * TRANSLATE_MAX_ROWS default): an import over it is REFUSED by schema,
 * never truncated.
 */
export const IMPORT_MAX_ROWS = 50_000;
export const IMPORT_MAX_CELL_CHARS = 10_000;
/* ------------------------------------------------------------------ */
/* Admin: AI model settings — packages/server/src/api/                 */
/* admin-ai-settings.ts, packages/client/src/features/admin.           */
/* ------------------------------------------------------------------ */
/**
 * Every AI function whose model an admin can override, in admin-page
 * display order. THE source of truth: the routes key their payloads and
 * their :functionKey enum off it, the /admin page derives one row per
 * entry, and the server's ai/model-settings.ts re-exports it. Adding a
 * function is this edit plus its FUNCTION_INFO copy (a Record keyed by
 * AiFunctionKey — omitting it is a compile error).
 */
export const AI_FUNCTIONS = [
    'translate-column',
    'split-sentence',
    // The two design-theme functions are separate entries, not one "design",
    // because they are different MODEL FAMILIES with different prices: a text
    // model writes the art direction, an image model renders from it. One
    // setting could never name both.
    'design-art-direction',
    'design-image',
    // Free-form Q&A about the script from a highlighted passage. Its own entry
    // rather than sharing the translate default because it defaults to
    // OpenRouter's AUTO router (`openrouter/auto`) — the question is arbitrary,
    // so no single model is right for it, and per-question routing is the point.
    'script-brainstorm',
    // Grouping the Original Script rows into titled sections and marking the
    // rows where the mood changes (api/sections.ts, F-139). Extractive
    // classification over the whole script, so the cheap translate-tier default
    // is the right fallback.
    'divide-sections',
    // The two halves of the thumbnail generator (F-130, api/thumbnails.ts) —
    // separate entries for the same reason the design pair is: a TEXT model
    // writes the five hooks from the script, an IMAGE model renders them. The
    // panel lets a user pick either per board; these are the fallbacks.
    'thumbnail-concepts',
    'thumbnail-image',
    // The presenter photo library's tagging pass (F-134, api/presenter-photos.ts):
    // a VISION-capable text model reads each uploaded photo of the presenter and
    // writes the expression tags the thumbnail run later chooses faces by. Its
    // own entry because it is the one text call here that carries an image
    // input — a text-only model on this key fails every tag.
    'portrait-tagging',
    // The board agent (F-144, api/agent.ts): a multi-round TOOL-CALLING loop
    // that reads the live sheet and attachments and proposes a plan of edits
    // the client applies. Its own entry because it is the one function that
    // needs a model with function calling, and because an agentic planner
    // earns a stronger model than the translate-tier default.
    'board-agent',
    // Naming a board created from an imported file (F-147, api/board-name.ts):
    // a few English keywords from the file name and its first lines. A tiny
    // translate-and-summarise call, so the cheap translate-tier default fits.
    'board-name',
    // The two AI steps of a layered file import (F-149, board-agent/split.ts),
    // each allocated its own model so the one that handles the step best can
    // be picked — and A/B'd — without touching the other. Cutting ONE text
    // into beats is extractive segmentation: the cheap translate-tier default.
    // Dividing a scene's direction, sound and translation across those beats
    // means reading two languages side by side: it defaults to the agent model.
    'import-beats',
    'import-align',
    // F-152: the description the desktop's YouTube upload page prefills — a
    // hook paragraph and a few key points in Traditional Chinese (Hong Kong),
    // written from the script (api/publish-kit.ts). Short prose from a sampled
    // script, so the cheap translate-tier default fits; a stronger model can
    // be set here when the wording needs more polish.
    'youtube-description',
    // The Social post page's captions (rainvic docs/social-post.md §6), for
    // the phone, which has no OpenRouter key of its own: the owner's Cantonese
    // SEO persona writes the shared caption from a brief and adapts it per
    // platform under that platform's playbook rule (api/social-caption.ts).
    // Short Traditional Chinese prose, so the translate-tier default fits.
    'social-caption',
];
export function isAiFunctionKey(value) {
    return typeof value === 'string' && AI_FUNCTIONS.includes(value);
}
/* ------------------------------------------------------------------ */
/* The AI model list — packages/server/src/api/ai-models.ts,            */
/* packages/client/src/features/ai-models.                              */
/* ------------------------------------------------------------------ */
/**
 * THE list of models Kola offers, workspace-wide and admin-editable
 * (2026-09-04). Every AI model dropdown in the app renders exactly this
 * list plus the resolved default — one list, one look, one place to change
 * it, which is the whole point: before this, the thumbnail panel carried a
 * hand-maintained ranking in client code (a redeploy to add a model) while
 * the brainstorm and sections pickers dumped the provider's entire ~400-entry
 * catalog, and neither agreed with the other about what was on offer.
 *
 * Persisted for the same reason the AI model settings and the `/` kinds are:
 * the owner adds a model the morning it lists and every writer's dropdown has
 * it, with no build. The provider catalog (`GET /api/ai/models`) survives as
 * the ADD FORM's suggestion source only — it is no longer what a picker
 * renders.
 */
export const AI_MODEL_KINDS = [
    // What the model MAKES, which is the only distinction a picker must not get
    // wrong: an image model on a text function returns nothing usable, and a
    // text model on the thumbnail render fails every frame. Detected from the
    // catalog when a model is added, and overridable by the admin — a listing
    // that declares no modality must not be undismissable as "text".
    'text',
    'image',
];
export function isAiModelKind(value) {
    return typeof value === 'string' && AI_MODEL_KINDS.includes(value);
}
/** Row-id prefix, so a stray id is recognizable in a log (cf. TEXT_KIND_ID_PREFIX). */
export const AI_MODEL_ENTRY_ID_PREFIX = 'aim_';
/** A dropdown people scan, not a catalog they search — a soft ceiling. */
export const AI_MODEL_LIST_MAX_COUNT = 60;
/** Optional nickname shown instead of the id ("nano-banana-pro"). */
export const AI_MODEL_LABEL_MAX_LENGTH = 60;
/** Same bound the AI routes have always applied to a `model` field. */
export const AI_MODEL_ID_MAX_LENGTH = 200;
/** First printable, non-space ASCII code point (space is 32). */
const AI_MODEL_ID_MIN_CODE = 33;
/** DEL — and the start of the range some fonts/terminals mis-render. */
const AI_MODEL_ID_DEL_CODE = 127;
/**
 * Non-empty, bounded, and free of whitespace/control characters — everything
 * else is left to the provider to accept or reject.
 *
 * Deliberately NOT a catalog check, and it stays that way now that the list
 * is what pickers render: adding a model id the provider listed an hour ago
 * (and whose catalog entry Kola's cached fetch has not seen) is exactly the
 * job this list exists to do. The server re-exports it as
 * `isPlausibleModelId` — the name every AI route already validates with.
 */
export function isAiModelId(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > AI_MODEL_ID_MAX_LENGTH)
        return false;
    for (let i = 0; i < trimmed.length; i += 1) {
        const code = trimmed.charCodeAt(i);
        if (code < AI_MODEL_ID_MIN_CODE || code === AI_MODEL_ID_DEL_CODE)
            return false;
    }
    return true;
}
/** A label is free text; only its length is anyone's business. */
export function isAiModelLabel(value) {
    return typeof value === 'string' && value.trim().length <= AI_MODEL_LABEL_MAX_LENGTH;
}
/** Two ids are the same model when they differ only by surrounding space. */
export function normalizeAiModelId(modelId) {
    return modelId.trim();
}
/* ------------------------------------------------------------------ */
/* Debug log + reports — packages/server/src/api/debug-reports.ts       */
/* ------------------------------------------------------------------ */
/**
 * On-screen debug log (client `core/debug-log.ts`) and the "send to the
 * project owner" report built from it. The client keeps a bounded ring of
 * entries — uncaught errors, rejected promises, console.error/warn output,
 * failed /api calls, collaboration-socket state changes — and any signed-in
 * user can post that ring plus a note to `POST /api/debug-reports`. Admins
 * (`admin.settings`) read the reports back on the admin page, and the server
 * logs every report as it lands so it also reaches Cloud Logging.
 *
 * Every cap below is enforced by the route's JSON schema (400 beyond) AND
 * applied by the client before it sends, so a report is never refused for
 * being too long — the client trims to exactly what the server accepts.
 */
export const DEBUG_LOG_LEVELS = ['error', 'warn', 'info'];
/** Entries kept client-side, and the most one report may carry. */
export const DEBUG_REPORT_MAX_ENTRIES = 200;
export const DEBUG_REPORT_MAX_SOURCE = 40;
export const DEBUG_REPORT_MAX_MESSAGE = 2_000;
export const DEBUG_REPORT_MAX_DETAIL = 8_000;
export const DEBUG_REPORT_MAX_NOTE = 2_000;
export const DEBUG_REPORT_MAX_CONTEXT_FIELD = 2_000;
/**
 * A keyword is ONE word: the `/` picker closes the moment the writer types a
 * space (ui/text-type.ts `slashQueryAt`), so a label with whitespace could
 * never be typed in full. Short, because it is a command people type.
 */
export const TEXT_KIND_LABEL_MAX_LENGTH = 24;
/** How many kinds a workspace may define — a menu, not a database. */
export const TEXT_KIND_MAX_COUNT = 30;
/**
 * Keywords the `/` menu already answers to, which an admin-defined kind may
 * not shadow: the three built-in kinds and the two built-in snippets
 * (ui/text-type.ts `TEXT_SNIPPETS` — duplicated here because the server is
 * what refuses the duplicate, and it never imports client code).
 */
export const RESERVED_TEXT_KIND_LABELS = [
    'narration',
    'sound',
    'visual',
    'pause',
    'impact',
];
/** Trim + case-fold: the form two labels are compared in. */
export function normalizeTextKindLabel(label) {
    return label.trim().toLowerCase();
}
/** One word, 1–24 characters, no whitespace and no `/` (it would re-open the menu). */
export function isTextKindLabel(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > TEXT_KIND_LABEL_MAX_LENGTH)
        return false;
    return !/[\s/]/.test(trimmed);
}
/** `#rrggbb` only — the value is interpolated into a stylesheet on every client. */
export function isTextKindColor(value) {
    return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}
/** Is `label` one of the keywords the menu already owns? */
export function isReservedTextKindLabel(label) {
    return RESERVED_TEXT_KIND_LABELS.includes(normalizeTextKindLabel(label));
}
/**
 * The `ty` value a kind is stored under. Prefixed so a kind id can never
 * collide with a built-in ("sound") however the id is generated, and so the
 * class name the client derives from it (`tt-k-…`) always starts with a letter.
 */
export const TEXT_KIND_ID_PREFIX = 'k-';
export function isTextKindId(value) {
    return (typeof value === 'string' &&
        value.startsWith(TEXT_KIND_ID_PREFIX) &&
        value.length > TEXT_KIND_ID_PREFIX.length &&
        /^[A-Za-z0-9_-]+$/.test(value));
}
/* ------------------------------------------------------------------ */
/* Presenter photo library (F-134) — packages/server/src/api/          */
/* presenter-photos.ts, packages/client/src/features/presenter-photos. */
/* ------------------------------------------------------------------ */
/** Row-id prefix, so a stray id is recognizable in a log (cf. AI_MODEL_ENTRY_ID_PREFIX). */
export const PRESENTER_PHOTO_ID_PREFIX = 'pp_';
/** Photos the library holds — enough expressions to choose between, not an archive. */
export const PRESENTER_PHOTO_MAX_COUNT = 80;
/** The tagger's one-line description of a photo — and the admin's edit of it. */
export const PRESENTER_PHOTO_SUMMARY_MAX_LENGTH = 200;
/** Keyword tags per photo, and each tag's length. */
export const PRESENTER_PHOTO_MAX_TAGS = 12;
export const PRESENTER_PHOTO_TAG_MAX_LENGTH = 40;
/**
 * Runtime guard for tags read back from storage or a response — the stored
 * value is JSON the route wrote, but a row written by a future build must
 * still parse, so unknown extra fields pass and missing required ones fail.
 */
export function isPresenterPhotoTags(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return (typeof v.summary === 'string' &&
        typeof v.mood === 'string' &&
        typeof v.framing === 'string' &&
        typeof v.gaze === 'string' &&
        Array.isArray(v.tags) &&
        v.tags.every((tag) => typeof tag === 'string') &&
        typeof v.usable === 'boolean' &&
        (v.issue === undefined || typeof v.issue === 'string'));
}
/** True when a photo may be offered to a thumbnail run: tagged and not vetoed. */
export function isPresenterPhotoOffered(photo) {
    return photo.tags !== undefined && photo.tags.usable;
}
export const API_TOKEN_NAME_MAX_CHARS = 80;
export const API_TOKEN_MAX_EXPIRY_DAYS = 365;
/** Live tokens one user may hold — the list is meant to be readable at a glance. */
export const API_TOKENS_PER_USER_MAX = 10;
//# sourceMappingURL=types.js.map